import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  Loader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Notification,
  NotificationType,
  RequiredFieldsText,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { ToggleField } from "@components/forms";
import { ApplicationId } from "@lib/constants";
import { ConfigurationKeys } from "@services/configuration";
import { services } from "@src/dependencies";
import { useApiRequest } from "@utils/http-client";

import { useConfigurations, useUpdateConfigurations } from "./api";
import { HeadsetLogoInput, OfflineModeToggle } from "./components";
import { getConfigurationsUpdated, validateConfigurations } from "./utils";

import "./vr-settings.modal.scss";

export const VrSettingsModal = ({ open, organization, onClose }) => {
  const initialConfigurationsRef = useRef();
  const { configurationService } = services;

  const [configurations, setConfigurations] = useState();
  const [isApplyEnabled, setIsApplyEnabled] = useState(true);

  const { loading: loadingConfigurations, getConfigurations } =
    useConfigurations(ApplicationId.TREK_VR, organization.id, {
      onSuccess: (data) => {
        initialConfigurationsRef.current = data;
        setConfigurations(data);
      },
    });

  const {
    loading: uploadingContent,
    sendRequest: uploadContent,
    error: uploadError,
    resetError: resetUploadError,
  } = useApiRequest(
    (file) =>
      configurationService
        .uploadContent(ApplicationId.TREK_VR, organization.id, file)
        .then((response) => {
          const { original } = response?.urls;
          onConfigurationChange(ConfigurationKeys.HEADSET_LOGO_URL, original);
        }),
    false,
  );

  const {
    loading: updatingConfiguration,
    success: updatingConfigurationSuccess,
    error: updateConfigurationError,
    resetStatus: resetUpdateConfigurationStatus,
    updateConfiguration,
  } = useUpdateConfigurations(ApplicationId.TREK_VR, organization.id, {
    onSuccess: () => {
      getConfigurations();
    },
  });

  const onHeadsetLogoChange = async (file) => {
    if (file) {
      await uploadContent(file);
    } else {
      // TODO: Add BE API later. For now we won't delete the stored value.
      onConfigurationChange(ConfigurationKeys.HEADSET_LOGO_URL);
    }
  };

  const onConfigurationChange = useCallback(
    (key, value) => {
      const configurationUpdated = { ...configurations[key], value: value };
      setConfigurations((prev) => ({ ...prev, [key]: configurationUpdated }));
    },
    [configurations],
  );

  const onApply = async () => {
    // Only send the configurations that have changed.
    const configurationsToUpdate = getConfigurationsUpdated(
      initialConfigurationsRef.current,
      configurations,
    );

    // Add the whitelisted URLs to the configurations
    // to update even if they haven't changed
    // since they are required by the BE endpoint.
    if (!configurationsToUpdate["whitelistedUrls"]) {
      configurationsToUpdate["whitelistedUrls"] =
        initialConfigurationsRef.current["whitelistedUrls"];
    }

    await updateConfiguration(configurationsToUpdate);
  };

  useEffect(() => {
    if (open) {
      getConfigurations();
    } else {
      setConfigurations();
      resetUpdateConfigurationStatus();
      resetUploadError();
      initialConfigurationsRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    // Get the configurations that have changed
    // If there are no changes, the form is not enabled.
    let enabled = false;
    const configurationToUpdate = getConfigurationsUpdated(
      initialConfigurationsRef.current,
      configurations,
    );

    // Validate the configurations
    if (Object.keys(configurationToUpdate).length > 0) {
      enabled = validateConfigurations(configurationToUpdate);
    }

    setIsApplyEnabled(enabled);
  }, [configurations]);

  useEffect(() => {
    // Close the notification after 1 second
    if (updatingConfigurationSuccess && !loadingConfigurations) {
      const timeoutId = setTimeout(() => {
        resetUpdateConfigurationStatus();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [updatingConfigurationSuccess, loadingConfigurations]);

  return createPortal(
    <Modal
      preventClose={updatingConfiguration || uploadingContent}
      modalClassName="vr-settings-modal"
      open={open}
      onClose={onClose}
    >
      {updatingConfiguration && <Loader overlay />}
      <ModalHeader title="Trek VR Settings" label={organization.name} />
      <ModalBody>
        {updateConfigurationError && (
          <Notification type={NotificationType.error}>
            There was an error updating the settings. Please try again.
          </Notification>
        )}
        {updatingConfigurationSuccess && (
          <Notification type={NotificationType.success}>
            Settings updated successfully.
          </Notification>
        )}
        <RequiredFieldsText />
        <HeadsetLogoInput
          loading={loadingConfigurations || uploadingContent}
          errors={
            uploadError?.data?.detail?.errors || uploadError?.data?.detail
          }
          onChange={onHeadsetLogoChange}
          value={configurations?.[ConfigurationKeys.HEADSET_LOGO_URL]?.value}
          hidden={!configurations?.[ConfigurationKeys.HEADSET_LOGO_URL]}
        />
        <OfflineModeToggle
          enabled={
            configurations?.[ConfigurationKeys.OFFLINE_MODE_ENABLED]?.value
          }
          value={
            configurations?.[ConfigurationKeys.OFFLINE_MODE_DURATION]?.value
          }
          description={
            configurations?.[ConfigurationKeys.OFFLINE_MODE_DURATION]
              ?.description
          }
          onToggle={(value) =>
            onConfigurationChange(ConfigurationKeys.OFFLINE_MODE_ENABLED, value)
          }
          onValueChange={(value) =>
            onConfigurationChange(
              ConfigurationKeys.OFFLINE_MODE_DURATION,
              value,
            )
          }
          loading={loadingConfigurations}
          hidden={
            !configurations?.[ConfigurationKeys.OFFLINE_MODE_ENABLED] ||
            !configurations?.[ConfigurationKeys.OFFLINE_MODE_DURATION]
          }
        />
        <ToggleField
          label="Skip Sim Exit Popup"
          description={
            configurations?.[ConfigurationKeys.SKIP_SIM_EXIT_POPUP]?.description
          }
          value={configurations?.[ConfigurationKeys.SKIP_SIM_EXIT_POPUP]?.value}
          onChange={(value) =>
            onConfigurationChange(ConfigurationKeys.SKIP_SIM_EXIT_POPUP, value)
          }
          loading={loadingConfigurations}
          required={true}
          hidden={!configurations?.[ConfigurationKeys.SKIP_SIM_EXIT_POPUP]}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          disabled={updatingConfiguration || uploadingContent}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          disabled={
            updatingConfiguration || uploadingContent || !isApplyEnabled
          }
          onClick={onApply}
          primary
        >
          Apply
        </Button>
      </ModalFooter>
    </Modal>,
    document.body,
  );
};
